import './daypicker.less';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import nb from 'date-fns/locale/nb';
import { addYears, parse as dateFnsParse, type Locale } from 'date-fns';
import { DayPicker, useInput } from 'react-day-picker';
import type { DayPickerSingleProps } from 'react-day-picker';
import type { TrackingProp } from '../types';
import { DateRange } from '@gjensidige/builders-icons';
import { Input, InputProps } from '../input/input';
import { getLabel, useTracking } from '../utils';
import { types } from './components/datepicker-type';
import * as utils from './components/datepicker-utils';
import styles from './datepicker.module.less';

/* NOTE ON IMPLEMENTATION:
 * onChange should trigger on all react-day-picker state changes,
 * but 'change' tracking should only happen if user blur input or close the datepicker
 * This is done to avoid too much tracking, while also providing a good DX
 */

let trackIfChanged = () => {}; // Used to only track changed when not open or focused
const MINUS_200 = addYears(Date.now(), -200);
const PLUSS_200 = addYears(Date.now(), +200);
const PARSE = 'd.M.yy, d-M-yy, dMyy'; // Scandinavian dmy ish

export type DatepickerProps = Omit<
  InputProps,
  'value' | 'onChange' | 'placeholder' | 'min' | 'max' | 'type'
> & {
  autoclose?: boolean;
  format?: string;
  locale?: Locale;
  max?: Date | string | number;
  min?: Date | string | number;
  onChange?: (
    date: Date | undefined,
    value: string,
    input: HTMLInputElement
  ) => void;
  parse?: string;
  placeholder?: string;
  reactDaypickerProps?: DayPickerSingleProps;
  tracking?: TrackingProp;
  type?: keyof typeof types;
  value?: Date | string | number;
};

export const Datepicker = forwardRef<HTMLInputElement, DatepickerProps>(
  function Datepicker(
    {
      autoclose = true,
      format,
      inputMode = 'decimal', // Automatic numeric keyboard
      locale = nb as unknown as Locale, // Typescript does not fully understand Locale
      max,
      min,
      onChange,
      parse = PARSE,
      placeholder = '',
      reactDaypickerProps,
      tracking = true,
      type = 'date',
      value: val,
      ...rest
    },
    ref
  ) {
    const track = useTracking(tracking);
    const innerRef = useRef<HTMLInputElement>(null);
    const valDate = val ? new Date(val) : undefined;
    const minDate = min ? new Date(min) : MINUS_200;
    const maxDate = max ? new Date(max) : PLUSS_200;
    const [open, setOpen] = utils.useOpen(innerRef);
    const { format: typeFormat, ...typeComponents } = types[type];
    const { inputProps, dayPickerProps, setSelected } = useInput({
      defaultSelected: valDate,
      format: format ?? typeFormat,
      locale,
      fromYear: minDate.getFullYear(),
      toYear: maxDate.getFullYear()
    });

    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement); // Forward innerRef
    utils.useOnPropChange(val, () => setSelected(valDate)); // Allow updating prop.value after first render

    // Track when opening and closing
    utils.useOnPropChange(open, () => {
      if (open)
        track({
          eventCategory: 'datepicker',
          eventAction: open ? 'open' : 'change',
          eventLabel: getLabel(innerRef.current)
        });
      else trackIfChanged();
    });

    // Create "fake" onChange event responding react-day-picker state change, and setup trackChange if open of focused
    utils.useOnPropChange(`${inputProps.value}`, (_, value) => {
      const input = innerRef.current;
      const focus = input && input === document.activeElement;

      if (input && onChange) onChange(dayPickerProps.selected, value, input);
      if (focus || open)
        trackIfChanged = () => {
          trackIfChanged = () => {}; // Reset to only track once
          track({
            eventCategory: 'datepicker',
            eventAction: 'change',
            eventLabel: getLabel(innerRef.current)
          });
        };
    });

    const onDayClick = () => autoclose && setOpen(!!innerRef.current?.focus());
    const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const date = dayPickerProps.selected || new Date();
      const value = `${inputProps.value}`.trim();
      const parsed = parse.split(',').reduce((acc, format) => {
        return (
          acc ||
          +dateFnsParse(value, format.trim(), date) ||
          +dateFnsParse(value, format.trim().replace(/yy\b/, 'yyyy'), date) // Also test for years with 4 digits
        ); // Using + to convert invalid dates to NaN
      }, 0);

      // If we parse better than react-day-picker, let our parsing trigger setSelected
      if (parsed) setSelected(new Date(parsed));
      else if (inputProps.onBlur) inputProps.onBlur(event);
      setTimeout(trackIfChanged); // Let React run setSelected first
    };

    return (
      <>
        <Input
          {...rest}
          {...inputProps}
          onBlur={utils.combineOn(onBlur, rest.onBlur)}
          onFocus={utils.combineOn(inputProps.onFocus, rest.onFocus)}
          autoComplete="off"
          inputMode={inputMode}
          invalid={
            rest.invalid ?? // Using ?? to let rest.invalid win whenever it is defined
            (dayPickerProps.selected &&
              (dayPickerProps.selected < minDate ||
                dayPickerProps.selected > maxDate))
          }
          placeholder={placeholder}
          ref={innerRef}
          tracking={false} // All tracking is done by useOpen
          type="text"
          suffix={
            <button
              aria-expanded={open}
              aria-label="📅"
              className={styles.suffix}
              type="button"
            >
              <DateRange />
            </button>
          }
        />
        {open && (
          <DayPicker
            ISOWeek
            captionLayout="dropdown"
            className={styles.dategrid}
            disabled={(date) => date < minDate || date > maxDate}
            showOutsideDays
            {...dayPickerProps}
            {...reactDaypickerProps}
            classNames={{
              button: styles.button,
              caption: styles.caption,
              ...reactDaypickerProps?.classNames
            }}
            components={{
              ...typeComponents,
              ...reactDaypickerProps?.components
            }}
            onDayClick={utils.combineOn(
              onDayClick,
              dayPickerProps.onDayClick,
              reactDaypickerProps?.onDayClick
            )}
          />
        )}
      </>
    );
  }
);
